import { ParameterGroup } from '../types/app';

// Scenario colors for charts
export const SCENARIO_COLORS = [
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#10B981', // Emerald
  '#F59E0B', // Amber
  '#8B5CF6', // Violet
  '#14B8A6', // Teal
  '#EAB308', // Yellow
  '#22C55E', // Green
  '#F43F5E', // Rose
  '#06B6D4', // Cyan
  '#0EA5E9', // Sky
  '#A855F7', // Purple
];

// Color palette for comparisons (distinct from scenario palette)
// Comparison palette extends scenario palette to ensure global uniqueness
export const COMPARISON_COLORS = [
  '#06B6D4', // Cyan
  '#EC4899', // Pink
  '#84CC16', // Lime
  '#F97316', // Orange (not Amber)
  '#0EA5E9', // Sky
  '#9333EA', // Purple Deep
  '#E11D48', // Rose Deep
  '#059669', // Emerald Deep
];

// Maximum number of scenarios allowed per subscription tier
export const MAX_FREE_SCENARIOS = 2;
export const MAX_PREMIUM_SCENARIOS = 6;

// Default time horizon
export const DEFAULT_TIME_HORIZON = 16;

// Parameter configurations for buying mode
export const BUYING_PARAMETER_GROUPS: ParameterGroup[] = [
  {
    title: 'Property Details',
    parameters: [
      {
        key: 'property_details.home_price',
        label: 'Home Price',
        min: 100000, // ~10th percentile entry-level
        max: 2000000, // ~90th percentile in many urban markets
        step: 10000,
        unit: '$',
        // Show full currency (no K-suffix) and accept full-value input
        formatValue: (value) => `$${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
        parseValue: (value) => parseInt(String(value).replace(/[^0-9]/g, '')),
      },
      {
        key: 'property_details.property_tax_rate',
        label: 'Property Tax Rate',
        min: 0.004, // 0.4%
        max: 0.035, // 3.5%
        step: 0.001,
        unit: '%',
        formatValue: (value) => `${(value * 100).toFixed(2)}%`,
        parseValue: (value) => parseFloat(value.replace('%', '')) / 100,
      },
      {
        key: 'property_details.hoa_monthly',
        label: 'Monthly HOA',
        min: 0,
        max: 1750,
        step: 25,
        unit: '$',
        formatValue: (value) => `$${value.toFixed(0)}`,
        parseValue: (value) => parseFloat(value.replace('$', '')),
      },
      {
        key: 'property_details.insurance_monthly',
        label: 'Monthly Insurance',
        min: 50,
        max: 3000,
        step: 25,
        unit: '$',
        formatValue: (value) => `$${value.toFixed(0)}`,
        parseValue: (value) => parseFloat(value.replace('$', '')),
      },
    ],
  },
  {
    title: 'Loan Details',
    parameters: [
      {
        key: 'loan_details.loan_type',
        label: 'Loan Type',
        min: 0,
        max: 1,
        step: 1,
        formatValue: (value) => (String(value).toLowerCase() === 'fha' ? 'FHA' : 'Conventional'),
        parseValue: (value) => (String(value).toLowerCase().includes('fha') ? 'fha' : 'conventional'),
      },
      {
        key: 'loan_details.mortgage_interest_rate',
        label: 'Interest Rate',
        min: 0.03,
        max: 0.10,
        step: 0.0025,
        unit: '%',
        formatValue: (value) => `${(value * 100).toFixed(2)}%`,
        parseValue: (value) => parseFloat(value.replace('%', '')) / 100,
      },
      {
        key: 'loan_details.down_payment_percent',
        label: 'Down Payment',
        min: 0.03,
        max: 0.40,
        step: 0.01,
        unit: '%',
        formatValue: (value) => `${(value * 100).toFixed(1)}%`,
        parseValue: (value) => parseFloat(value.replace('%', '')) / 100,
      },
      {
        key: 'loan_details.loan_term_years',
        label: 'Loan Term',
        min: 10,
        max: 30,
        step: 5,
        unit: ' years',
        formatValue: (value) => `${value} years`,
        parseValue: (value) => parseInt(value.replace(' years', '')),
      },
      {
        key: 'loan_details.pmi_rate',
        label: 'PMI Rate',
        min: 0,
        max: 0.015,
        step: 0.001,
        unit: '%',
        formatValue: (value) => `${(value * 100).toFixed(2)}%`,
        parseValue: (value) => parseFloat(value.replace('%', '')) / 100,
      },
    ],
  },
  {
    title: 'Market Assumptions',
    parameters: [
      {
        key: 'market_assumptions.property_appreciation_rate',
        label: 'Property Appreciation',
        min: -0.02,
        max: 0.08,
        step: 0.005,
        unit: '%',
        formatValue: (value) => `${(value * 100).toFixed(1)}%`,
        parseValue: (value) => parseFloat(value.replace('%', '')) / 100,
      },
    ],
  },
  {
    title: 'Rental Income',
    parameters: [
      {
        key: 'buying_params.rental_strategy.rent_increase_rate',
        label: 'Rent Increase Rate',
        min: 0,
        max: 0.06,
        step: 0.005,
        unit: '%',
        formatValue: (value) => `${(value * 100).toFixed(1)}%`,
        parseValue: (value) => parseFloat(value.replace('%', '')) / 100,
      },
      {
        key: 'buying_params.rental_strategy.monthly_income',
        label: 'Monthly Income',
        min: 0,
        max: 6500,
        step: 100,
        unit: '$',
        formatValue: (value) => `$${value.toFixed(0)}`,
        parseValue: (value) => parseFloat(value.replace('$', '')),
      },
      {
        key: 'buying_params.rental_strategy.occupancy_rate',
        label: 'Occupancy Rate',
        min: 0.5,
        max: 1.0,
        step: 0.05,
        unit: '%',
        formatValue: (value) => `${(value * 100).toFixed(0)}%`,
        parseValue: (value) => parseFloat(value.replace('%', '')) / 100,
      },
      {
        key: 'buying_params.rental_strategy.duration_years',
        label: 'Duration',
        min: 1,
        max: 30,
        step: 1,
        unit: ' years',
        formatValue: (value) => `${value} years`,
        parseValue: (value) => parseInt(value.replace(' years', '')),
      },
    ],
  },
];

// Parameter configurations for renting mode
export const RENTING_PARAMETER_GROUPS: ParameterGroup[] = [
  {
    title: 'Rent Details',
    parameters: [
      {
        key: 'renting_params.current_rent_living_alone',
        label: 'Monthly Rent',
        min: 400,
        max: 6000,
        step: 50,
        unit: '$',
        formatValue: (value) => `$${value.toFixed(0)}`,
        parseValue: (value) => parseFloat(value.replace('$', '')),
      },
    ],
  },
  {
    title: 'Market Assumptions',
    parameters: [
      {
        key: 'market_assumptions.investment_return_rate',
        label: 'Investment Return (Stocks)',
        min: 0.03,
        max: 0.12,
        step: 0.005,
        unit: '%',
        formatValue: (value) => `${(value * 100).toFixed(1)}%`,
        parseValue: (value) => parseFloat(value.replace('%', '')) / 100,
      },
      {
        key: 'market_assumptions.rent_increase_rate',
        label: 'Rent Increase Rate',
        min: 0,
        max: 0.06,
        step: 0.005,
        unit: '%',
        formatValue: (value) => `${(value * 100).toFixed(1)}%`,
        parseValue: (value) => parseFloat(value.replace('%', '')) / 100,
      },
    ],
  },
  {
    title: 'Rental Income',
    parameters: [
      {
        key: 'renting_params.rental_strategy.rent_increase_rate',
        label: 'Rent Increase Rate (Sublease)',
        min: 0,
        max: 0.06,
        step: 0.005,
        unit: '%',
        formatValue: (value) => `${(value * 100).toFixed(1)}%`,
        parseValue: (value) => parseFloat(value.replace('%', '')) / 100,
      },
      {
        key: 'renting_params.rental_strategy.monthly_income',
        label: 'Monthly Income',
        min: 0,
        max: 5000,
        step: 100,
        unit: '$',
        formatValue: (value) => `$${value.toFixed(0)}`,
        parseValue: (value) => parseFloat(value.replace('$', '')),
      },
      {
        key: 'renting_params.rental_strategy.occupancy_rate',
        label: 'Occupancy Rate',
        min: 0.5,
        max: 1.0,
        step: 0.05,
        unit: '%',
        formatValue: (value) => `${(value * 100).toFixed(0)}%`,
        parseValue: (value) => parseFloat(value.replace('%', '')) / 100,
      },
      {
        key: 'renting_params.rental_strategy.duration_years',
        label: 'Duration',
        min: 1,
        max: 20,
        step: 1,
        unit: ' years',
        formatValue: (value) => `${value} years`,
        parseValue: (value) => parseInt(value.replace(' years', '')),
      },
    ],
  },
];

// Tooltip content for individual parameters
export const PARAMETER_TOOLTIPS = {
  // Property Details
  'property_details.home_price': 'Total cost of the home',
  'property_details.property_tax_rate': 'Annual property tax rate as a percentage of home value',
  'property_details.hoa_monthly': 'Monthly homeowners association fees',
  'property_details.insurance_monthly': 'Monthly homeowners insurance premium',
  
  // Loan Details
  'loan_details.loan_type': 'FHA loans have lower down payment requirements but require MIP. Conventional loans require 20% down to avoid PMI.',
  'loan_details.mortgage_interest_rate': 'Annual interest rate for your mortgage',
  'loan_details.down_payment_percent': 'Percentage of home price paid upfront',
  'loan_details.loan_term_years': 'Number of years to repay the loan',
  'loan_details.pmi_rate': 'Annual private mortgage insurance rate (if down payment < 20%)',
  'loan_details.ufmip_rate': 'Upfront mortgage insurance premium rate for FHA loans',
  'loan_details.finance_ufmip': 'Whether to add UFMIP to the loan amount',
  'loan_details.rate_buydown_points': 'Number of points paid to reduce interest rate',
  'loan_details.rate_buydown_rate_per_point': 'Interest rate reduction per point paid',
  
  // Market Assumptions
  'market_assumptions.property_appreciation_rate': 'Expected annual home value appreciation',
  'market_assumptions.investment_return_rate': 'Expected annual return on stock market investments',
  'market_assumptions.rent_increase_rate': 'Expected annual rent increase rate',
  
  // Rental Income
  'renting_params.rental_strategy.rent_increase_rate': 'Annual rent increase rate for subleasing',
  'renting_params.rental_strategy.monthly_income': 'Monthly rental income from subleasing',
  'renting_params.rental_strategy.occupancy_rate': 'Percentage of time the property is occupied by tenants',
  'renting_params.rental_strategy.duration_years': 'Number of years to rent out the property',
  
  // Rent Details
  'renting_params.current_rent_living_alone': 'Your current monthly rent payment',
  
  // Transaction Costs
  'transaction_costs.realtor_fee_percent': 'Real estate agent commission as percentage of sale price',
  'transaction_costs.closing_costs_percent': 'Closing costs as percentage of home price',
  'transaction_costs.seller_concessions_percent': 'Seller concessions as percentage of home price',
  'transaction_costs.inspection_costs': 'Home inspection and appraisal costs',
  'transaction_costs.title_insurance': 'Title insurance and escrow fees',
  'transaction_costs.recording_fees': 'Government recording and transfer fees',
  'transaction_costs.other_costs': 'Any other one-time costs',
};
